import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Link as RouterLink, Outlet, useLocation } from 'react-router-dom';
import Box from '@mui/material/Box';
import Breadcrumbs from '@mui/material/Breadcrumbs';
import Drawer from '@mui/material/Drawer';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import Link from '@mui/material/Link';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Toolbar from '@mui/material/Toolbar';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined';
import PaletteOutlinedIcon from '@mui/icons-material/PaletteOutlined';
import LightModeOutlinedIcon from '@mui/icons-material/LightModeOutlined';
import DarkModeOutlinedIcon from '@mui/icons-material/DarkModeOutlined';
import { brand } from '@/config/navigation';
import { useColorMode } from '@/hooks/useColorMode';
import { useCurrentAcademicYear } from '@/hooks/useCurrentAcademicYear';
import { useAppSettingsSync } from '@/hooks/useAppSettingsSync';
import { useRefetchOnAcademicYearChange } from '@/hooks/useRefetchOnAcademicYearChange';
import { useGlobalSelector } from '@/hooks/useGlobalState';
import { useRole } from '@/hooks/useRole';
import { formattingKey } from '@/lib/format';
import { Loading } from '@/components/feedback/Loading';
import { AcademicYearSwitcher } from './AcademicYearSwitcher';
import { SidebarNav } from './SidebarNav';
import { CommandPalette } from './CommandPalette';
import { ProfileMenu } from './ProfileMenu';
import { RoleSwitcher } from './RoleSwitcher';
import { SchoolSwitcher } from './SchoolSwitcher';
import { ThemeDrawer } from './ThemeDrawer';
import { WorkspaceCard } from './WorkspaceCard';
import { breadcrumbForPath, type BreadcrumbEntry } from './navUtils';
import { PageBreadcrumbsContext } from './pageBreadcrumbs';
import { useNavigation } from '@/hooks/useNavigation';
import { portalRoutes } from '@/routes';
import { preloadRoutes } from '@/routes/preloadRoutes';

/**
 * Production builds fetch the role's other pages in the background once the first one is up, so
 * opening a page later downloads nothing. The dev server serves every source file separately, so
 * there it is off unless VITE_PRELOAD_PAGES=true.
 */
const PRELOAD_PAGES = import.meta.env.PROD || import.meta.env.VITE_PRELOAD_PAGES === 'true';
import { CamptainMark } from '@/components/brand/CamptainMark';

const DRAWER_WIDTH = 260;
const COLLAPSED_WIDTH = 76;
const COLLAPSE_STORAGE_KEY = 'camptain.sidebar-collapsed';

function BrandMark({ collapsed }: { collapsed: boolean }) {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.25,
        px: collapsed ? 1.5 : 2.5,
        py: 1.5,
        justifyContent: collapsed ? 'center' : 'flex-start',
      }}
    >
      {/* The name is shown beside the mark when expanded, so it's decorative then. */}
      <CamptainMark size={36} title={collapsed ? brand.name : undefined} />
      {!collapsed && (
        <Typography variant="h6" component="span" noWrap sx={{ fontWeight: 800, flex: 1 }}>
          {brand.name}
        </Typography>
      )}
    </Box>
  );
}

/**
 * Sidebar search — filters the nav list below as you type, right here (no
 * dialog). Sits above the nav list, outside its scroll container, so it stays
 * put while the list scrolls. In the collapsed icon rail there's no room to
 * type, so the icon just expands the sidebar so the input becomes usable.
 */
function SidebarSearch({
  collapsed,
  value,
  onChange,
  onExpand,
}: {
  collapsed: boolean;
  value: string;
  onChange: (value: string) => void;
  onExpand?: () => void;
}) {
  const sidebar = useTheme().tokens.component.sidebar;

  if (collapsed) {
    return (
      <Box
        sx={{ display: 'flex', justifyContent: 'center', px: 1.5, pt: 1, pb: 0.5, flexShrink: 0 }}
      >
        <Tooltip title="Search menus" placement="right">
          <IconButton onClick={onExpand} size="small" aria-label="Search menus">
            <SearchOutlinedIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>
    );
  }

  return (
    <Box sx={{ px: 1.5, pt: 1, pb: 0.5, flexShrink: 0 }}>
      <TextField
        size="small"
        fullWidth
        placeholder="Search menus"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        slotProps={{
          htmlInput: { 'aria-label': 'Search menus' },
          input: {
            startAdornment: (
              <InputAdornment position="start">
                <SearchOutlinedIcon sx={{ fontSize: 18, color: sidebar.mutedText }} />
              </InputAdornment>
            ),
            endAdornment: value ? (
              <InputAdornment position="end">
                <IconButton
                  size="small"
                  aria-label="Clear search"
                  onClick={() => onChange('')}
                  sx={{ p: 0.25, color: sidebar.mutedText }}
                >
                  <CloseIcon sx={{ fontSize: 14 }} />
                </IconButton>
              </InputAdornment>
            ) : undefined,
          },
        }}
        sx={{
          '& .MuiOutlinedInput-root': {
            borderRadius: 1.5,
            fontSize: '0.8125rem',
            bgcolor: sidebar.hoverBackground,
            '& fieldset': { borderColor: 'transparent' },
            '&:hover fieldset': { borderColor: sidebar.border },
            '&.Mui-focused fieldset': { borderColor: 'primary.main' },
          },
          '& .MuiOutlinedInput-input': { py: 1, color: sidebar.text },
        }}
      />
    </Box>
  );
}

/** Overlay sidebar for mobile/tablet — one continuous panel, as an overlay drawer conventionally is. */
function MobileDrawerContent({
  onNavigate,
  trail,
}: {
  onNavigate: () => void;
  trail: string | null;
}) {
  const sidebar = useTheme().tokens.component.sidebar;
  const [query, setQuery] = useState('');
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        color: sidebar.text,
        '& .MuiDivider-root': { borderColor: sidebar.border },
        '& .MuiIconButton-root': { color: sidebar.mutedText },
      }}
    >
      <BrandMark collapsed={false} />
      <WorkspaceCard trail={trail} />
      <SidebarSearch collapsed={false} value={query} onChange={setQuery} />
      <Box sx={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
        <SidebarNav onNavigate={onNavigate} filter={query} />
      </Box>
      <Box sx={{ borderTop: `1px solid ${sidebar.border}` }}>
        <RoleSwitcher />
      </Box>
    </Box>
  );
}

/**
 * Desktop sidebar — one full-height card (brand, workspace, search, nav, account),
 * with a docked toggle to collapse the whole column to an icon rail.
 */
function DesktopSidebar({
  width,
  collapsed,
  onToggleCollapse,
  trail,
}: {
  width: number;
  collapsed: boolean;
  onToggleCollapse: () => void;
  trail: string | null;
}) {
  const theme = useTheme();
  const sidebar = theme.tokens.component.sidebar;
  const [query, setQuery] = useState('');

  return (
    <Box
      sx={{
        display: { xs: 'none', lg: 'flex' },
        flexDirection: 'column',
        width,
        flexShrink: 0,
        height: '100%',
        minHeight: 0,
        bgcolor: sidebar.background,
        color: sidebar.text,
        borderRight: `1px solid ${sidebar.border}`,
        transition: theme.transitions.create('width', {
          duration: theme.transitions.duration.shorter,
        }),
        '& .MuiDivider-root': { borderColor: sidebar.border },
        '& .MuiIconButton-root': { color: sidebar.mutedText },
      }}
      aria-label="Sidebar"
    >
      <BrandMark collapsed={collapsed} />
      <WorkspaceCard collapsed={collapsed} onToggleCollapse={onToggleCollapse} trail={trail} />
      <SidebarSearch
        collapsed={collapsed}
        value={query}
        onChange={setQuery}
        onExpand={onToggleCollapse}
      />
      <Box sx={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
        <SidebarNav collapsed={collapsed} filter={query} />
      </Box>
      <Box sx={{ borderTop: `1px solid ${sidebar.border}` }}>
        <RoleSwitcher collapsed={collapsed} />
      </Box>
    </Box>
  );
}

/** Responsive application frame: floating sidebar + header + content cards on
 * desktop (sidebar collapsible to an icon rail), temporary overlay drawer on
 * tablet/mobile, single mount for the whole app. */
export function AppShell() {
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('lg'));
  const { mode, toggle } = useColorMode();
  const { pathname } = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(
    () => localStorage.getItem(COLLAPSE_STORAGE_KEY) === '1',
  );
  const [commandOpen, setCommandOpen] = useState(false);
  const [themeOpen, setThemeOpen] = useState(false);

  useEffect(() => {
    const handler = (e: globalThis.KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setCommandOpen(true);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  useEffect(() => {
    localStorage.setItem(COLLAPSE_STORAGE_KEY, collapsed ? '1' : '0');
  }, [collapsed]);

  const { portal, portals, roleCode, roleLabel, home: homePath } = useRole();
  // Keyed by the portal names: the list is rebuilt on every render.
  const portalsKey = portals.join(',');
  useEffect(() => {
    if (!PRELOAD_PAGES) return undefined;
    return preloadRoutes(portalsKey.split(',').flatMap((p) => portalRoutes[p as keyof typeof portalRoutes] ?? []));
  }, [portalsKey]);
  // The school's Global Settings drive every formatter; a change remounts the page so
  // module-level column definitions pick it up.
  useAppSettingsSync();
  const settingsKey = useGlobalSelector(({ appSettings }) => formattingKey(appSettings));
  // The working academic year is set from the school's current year at start and on every
  // school switch; a change remounts the page so it reloads for the new year. While it is
  // being resolved the page is held back, so it neither queries without a year nor mounts twice.
  const { isLoading: yearResolving } = useCurrentAcademicYear();
  const workingYearId = useGlobalSelector((s) => s.activeAcademicYearId);
  const branchKey = useGlobalSelector((s) =>
    s.access
      ? s.access.activeOrganization.organizationId + ':' + s.access.branchId
      : s.activeSchoolId,
  );
  // Cached data was fetched for the old year; switching years refetches it.
  useRefetchOnAcademicYearChange();
  // The student and parent portals view the working year; the others may change it.
  const officeRole = portal !== 'student' && portal !== 'parent';
  const navigation = useNavigation(portal, roleCode);
  const [pageCrumbs, setPageCrumbs] = useState<BreadcrumbEntry[] | null>(null);
  const menuCrumbs = useMemo(() => breadcrumbForPath(navigation, pathname), [navigation, pathname]);
  const crumbs = pageCrumbs ?? menuCrumbs;
  const pageTitle = crumbs[crumbs.length - 1]?.label ?? roleLabel;
  // Plain-text location trail for the sidebar's workspace card — omitted at the
  // role's own home page, where there's nothing beyond the role itself to show.
  const trail = crumbs.length > 1 ? crumbs.map((c) => c.label).join(' › ') : null;

  useEffect(() => {
    document.title = `${pageTitle} · Camptain`;
  }, [pageTitle]);

  // Close the temporary drawer whenever the route changes. Deriving this from
  // `pathname` during render (rather than in an effect) avoids a cascading
  // render and keeps the drawer closed on back/forward navigation too.
  const [lastPath, setLastPath] = useState(pathname);
  if (pathname !== lastPath) {
    setLastPath(pathname);
    if (mobileOpen) setMobileOpen(false);
  }

  const pageRegionRef = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => {
    const region = pageRegionRef.current;
    if (!region) return;
    const resize = () => {
      // The page surface has 4px padding on each edge. Use the actual available
      // height so header size, viewport changes and mobile chrome are accounted for.
      region.style.setProperty(
        '--page-content-height',
        `${Math.max(0, region.clientHeight - 8)}px`,
      );
    };
    resize();
    const observer = new ResizeObserver(resize);
    observer.observe(region);
    window.addEventListener('resize', resize);
    return () => {
      observer.disconnect();
      window.removeEventListener('resize', resize);
    };
  }, []);

  const railWidth = collapsed ? COLLAPSED_WIDTH : DRAWER_WIDTH;

  return (
    <Box
      sx={{
        display: 'flex',
        height: '100dvh',
        overflow: 'hidden',
        bgcolor: 'background.default',
      }}
    >
      {/* Mobile / tablet: temporary overlay drawer — never collapses, always full-height */}
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={() => setMobileOpen(false)}
        ModalProps={{ keepMounted: true }}
        sx={{
          display: { xs: 'block', lg: 'none' },
          '& .MuiDrawer-paper': {
            width: DRAWER_WIDTH,
            boxSizing: 'border-box',
            bgcolor: theme.tokens.component.sidebar.background,
          },
        }}
      >
        <MobileDrawerContent onNavigate={() => setMobileOpen(false)} trail={trail} />
      </Drawer>

      <DesktopSidebar
        width={railWidth}
        collapsed={collapsed}
        onToggleCollapse={() => setCollapsed((c) => !c)}
        trail={trail}
      />

      <Box
        component="main"
        sx={{
          flex: 1,
          minWidth: 0,
          display: 'flex',
          flexDirection: 'column',
          minHeight: 0,
        }}
      >
        <Box
          sx={{
            flexShrink: 0,
            bgcolor: 'background.paper',
            borderBottom: `1px solid ${theme.palette.divider}`,
          }}
        >
          <Toolbar
            variant="dense"
            sx={{
              minHeight: '52px !important',
              px: { xs: 1.5, sm: 2.5 },
              display: 'flex',
              alignItems: 'center',
              columnGap: 2,
            }}
          >
            <Stack direction="row" spacing={1} sx={{ alignItems: 'center', minWidth: 0, flex: 1 }}>
              {!isDesktop && (
                <IconButton
                  edge="start"
                  aria-label="Open navigation menu"
                  onClick={() => setMobileOpen(true)}
                  size="small"
                >
                  <MenuIcon fontSize="small" />
                </IconButton>
              )}
              <Breadcrumbs
                component="h1"
                separator={<NavigateNextIcon sx={{ fontSize: 16 }} />}
                sx={{ minWidth: 0, '& .MuiBreadcrumbs-ol': { flexWrap: 'nowrap' } }}
              >
                {/* The role is already clear from the sidebar and role switcher, so it
                    only shows on the role's home page, where there is no other trail. */}
                {crumbs.length === 0 ? (
                  <Typography variant="body2" noWrap sx={{ fontWeight: 700 }} color="text.primary">
                    {roleLabel}
                  </Typography>
                ) : (
                  <Link
                    component={RouterLink}
                    to={homePath}
                    underline="hover"
                    variant="body2"
                    color="text.secondary"
                    noWrap
                  >
                    {roleLabel}
                  </Link>
                )}
                {crumbs.slice(0, -1).map((crumb, i) =>
                  crumb.to ? (
                    <Link
                      key={`${crumb.label}-${i}`}
                      component={RouterLink}
                      to={crumb.to}
                      underline="hover"
                      variant="body2"
                      color="text.secondary"
                      noWrap
                    >
                      {crumb.label}
                    </Link>
                  ) : (
                    <Typography
                      key={`${crumb.label}-${i}`}
                      variant="body2"
                      noWrap
                      color="text.secondary"
                    >
                      {crumb.label}
                    </Typography>
                  ),
                )}
                {crumbs.length > 0 && (
                  <Typography variant="body2" noWrap sx={{ fontWeight: 700 }} color="text.primary">
                    {pageTitle}
                  </Typography>
                )}
              </Breadcrumbs>
            </Stack>

            <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center', flexShrink: 0 }}>
              {/* Search sits with the other header tools, just before the school switcher. */}
              <Box
                component="button"
                type="button"
                onClick={() => setCommandOpen(true)}
                aria-label="Search"
                sx={{
                  display: { xs: 'none', sm: 'flex' },
                  alignItems: 'center',
                  gap: 1,
                  px: 1.25,
                  height: 34,
                  width: { sm: 200, md: 260 },
                  mr: 1,
                  borderRadius: 1,
                  border: `1px solid ${theme.palette.divider}`,
                  bgcolor: 'transparent',
                  color: 'text.secondary',
                  font: 'inherit',
                  cursor: 'pointer',
                  '&:hover': { borderColor: 'primary.main' },
                }}
              >
                <SearchOutlinedIcon fontSize="small" />
                <Typography variant="body2" noWrap sx={{ flex: 1, textAlign: 'left' }}>
                  Search…
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    border: `1px solid ${theme.palette.divider}`,
                    borderRadius: 0.75,
                    px: 0.5,
                    lineHeight: 1.6,
                  }}
                >
                  {navigator.userAgent.includes('Mac') ? '⌘K' : 'Ctrl K'}
                </Typography>
              </Box>
              <IconButton
                aria-label="Search"
                size="small"
                onClick={() => setCommandOpen(true)}
                sx={{ display: { xs: 'inline-flex', sm: 'none' } }}
              >
                <SearchOutlinedIcon fontSize="small" />
              </IconButton>
              {/* Students and parents see the year but may not change it. */}
              <AcademicYearSwitcher readOnly={!officeRole} />
              <SchoolSwitcher />
              <Tooltip title="Theme">
                <IconButton
                  onClick={() => setThemeOpen(true)}
                  aria-label="Open theme settings"
                  size="small"
                >
                  <PaletteOutlinedIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title={mode === 'light' ? 'Switch to dark mode' : 'Switch to light mode'}>
                <IconButton onClick={toggle} aria-label="Toggle colour mode" size="small">
                  {mode === 'light' ? (
                    <DarkModeOutlinedIcon fontSize="small" />
                  ) : (
                    <LightModeOutlinedIcon fontSize="small" />
                  )}
                </IconButton>
              </Tooltip>
              <ProfileMenu />
            </Stack>
          </Toolbar>
        </Box>

        <Box
          data-page-loading-region
          ref={pageRegionRef}
          sx={{
            flex: 1,
            minHeight: 0,
            position: 'relative',
            overflow: 'hidden',
            bgcolor: 'background.default',
          }}
        >
          <Box sx={{ height: '100%', overflowY: 'auto' }}>
            <Box
              sx={{
                p: { xs: 0.5, sm: 0.5, lg: 0.5 },
                minHeight: '100%',
                display: 'flex',
                flexDirection: 'column',
                boxSizing: 'border-box',
                bgcolor: 'background.paper',
                '& > :not([hidden]):not(.MuiAlert-root)': {
                  flex: '1 0 auto',
                  minHeight: 'var(--page-content-height, 0px)',
                },
                '& > .MuiAlert-root': { flexShrink: 0 },
                width: '100%',
                mx: 'auto',
              }}
            >
              <PageBreadcrumbsContext.Provider value={setPageCrumbs}>
                {yearResolving ? (
                  <Loading label="Loading academic year…" />
                ) : (
                  <Outlet key={`${branchKey}|${settingsKey}|${workingYearId ?? 'none'}`} />
                )}
              </PageBreadcrumbsContext.Provider>
            </Box>
          </Box>
        </Box>
      </Box>

      <CommandPalette open={commandOpen} onClose={() => setCommandOpen(false)} />
      <ThemeDrawer open={themeOpen} onClose={() => setThemeOpen(false)} />
    </Box>
  );
}
